import { PROJECT_STATUSES, ProjectLayoutModel, User } from '@paragon/contract-library';
import { getUnknownUser, getUserDisplayName } from '@paragon/login-tools';
import { naturalSort } from '@paragon/utilities';

import { parseDueDate } from '../../dueDates';

type SortKey = number | string | null;

interface ProjectSortContext {
  readonly membersByEmail: Readonly<Record<string, User>>;
}

export const ORDER_BY_MAPPINGS = {
  createdAt: (project) => Date.parse(project.createdAt),
  name: (project) => blankToNull(project.name),
  contact: (project) => blankToNull(project.contactName),
  contactEmail: (project) => blankToNull(project.contactEmail),
  assignee: (project, context) => getAssigneeSortKey(project.assigneeEmail, context),
  dueDate: (project) => parseDueDate(project.dueDate)?.valueOf() ?? null,
  status: (project) => {
    const statusOrder = PROJECT_STATUSES.indexOf(project.status);
    return statusOrder === -1 ? null : statusOrder;
  },
} satisfies Record<string, (project: ProjectLayoutModel, context: ProjectSortContext) => SortKey>;

export type SortableColumnName = keyof typeof ORDER_BY_MAPPINGS;

export function isSortableColumn(columnName: string): columnName is SortableColumnName {
  return columnName in ORDER_BY_MAPPINGS;
}

/**
 * Orders projects by a column the way the grid shows them: text naturally and regardless of case ("Lot 9" before
 *   "Lot 10"), blanks last in either direction, and ties newest first so rows hold still as the order is reversed or
 *   projects are created.
 */
export function sortProjects(
  projects: readonly ProjectLayoutModel[],
  columnName: SortableColumnName,
  order: 'asc' | 'desc',
  context: ProjectSortContext,
): ProjectLayoutModel[] {
  const getSortKey = ORDER_BY_MAPPINGS[columnName];

  return projects
    .map((project) => {
      const sortKey = getSortKey(project, context);
      const createdAt = columnName === 'createdAt' ? (sortKey as number) : ORDER_BY_MAPPINGS.createdAt(project);
      return { project, sortKey, createdAt };
    })
    .sort(
      (a, b) =>
        compareSortKeys(a.sortKey, b.sortKey, order) ||
        b.createdAt - a.createdAt ||
        compareExact(a.project.guid, b.project.guid),
    )
    .map(({ project }) => project);
}

function compareSortKeys(a: SortKey, b: SortKey, order: 'asc' | 'desc'): number {
  // blanks trail in both directions as in a spreadsheet so they never push the values being looked for off the page
  if (a == null || b == null) return Number(a == null) - Number(b == null);

  const comparison = typeof a === 'number' && typeof b === 'number' ? a - b : naturalSort(String(a), String(b));
  return order === 'asc' ? comparison : -comparison;
}

function compareExact(a: string, b: string): number {
  return a < b ? -1 : a > b ? 1 : 0;
}

function blankToNull(value: string | null | undefined): string | null {
  return value?.trim() || null;
}

function getAssigneeSortKey(assigneeEmail: string | null, context: ProjectSortContext): string | null {
  if (!assigneeEmail) return null;

  return getUserDisplayName(context.membersByEmail[assigneeEmail] ?? getUnknownUser(assigneeEmail));
}
